import { PlaylistItem } from "@components/Playlist/PlaylistsPageComponents/AddContent";
import QueryFetchStatePage from "@components/QueryFetchStatePage";
import { useContentPreviewDialog } from "@components/shared/ContentPreviewDialog";
import { Button } from "@components/ui/button";
import { useAssets } from "@hooks/useAssets";
import { useAuth } from "@hooks/useAuth";
import useDialog, { DialogProvider } from "@hooks/useDialog";
import useGzLog from "@hooks/useGzLog";
import { useLayouts } from "@hooks/useLayouts";
import { usePlaylists } from "@hooks/usePlaylists";
import Link from "next/link";
import { usePublicAssets } from "@hooks/usePublicAssets";
import { usePublicPlaylists } from "@hooks/usePublicPlaylists";
import { usePublicTemplates } from "@hooks/usePublicTemplates";
import { usePublicVideos } from "@hooks/usePublicVideos";
import { ScreenWithPlayingInfo } from "@hooks/useScreens";
import { ScreenPlayingOptionType } from "@hooks/useScreens/types";
import { useTemplates } from "@hooks/useTemplates";
import { useVideoAssets } from "@hooks/useVideoAssets";
import { useUpdateScreenPlaying } from "@utils/queries";
import React, { ReactNode, useEffect, useState } from "react";
import { BsImages, BsLayoutWtf } from "react-icons/bs";
import { ImFileVideo } from "react-icons/im";
import {
  MdArrowForward,
  MdClear,
  MdDone,
  MdInfoOutline,
  MdOutlinePlaylistPlay,
  MdRemoveRedEye,
} from "react-icons/md";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";
import CustomCarousel from "../CustomCarousel";
import { SyncContentWithLocationButton } from "../SyncContentWithLocationButton";
import SelectCustomLayout from "./SelectCustomLayout";
import SelectImages from "./SelectImages";
import SelectPlaylist from "./SelectPlaylist";
import SelectVideo from "./SelectVideo";

const _types = ["asset", "video", "playlist", "layout"] as const;
type SelectedType = (typeof _types)[number];

export type UpdateScreenPlaying = ReturnType<typeof useUpdateScreenPlaying>["mutate"];
type OmitLiteral<T extends string, L extends T> = T extends L ? never : T

type Props = {
  screen: ScreenWithPlayingInfo;
  orgId: string;
  updateScreenPlaying: UpdateScreenPlaying;
  onClose: () => void;
};

function SelectScreenContentDialog(props: Props) {
  const { playlistsFetchStatus, getPlaylists } = usePlaylists()
  const { playlistsFetchStatus: publicPlaylistsFetchStatus, getPlaylists: getPublicPlaylists } = usePublicPlaylists()
  const { assetsFetchStatus, getAssets } = useAssets()
  const { assetsFetchStatus: publicAssetsFetchStatus, getAssets: getPublicAssets } = usePublicAssets()
  const { layoutsFetchStatus, getLayouts } = useLayouts()
  const { templatesFetchStatus, getTemplates } = useTemplates()
  const { templatesFetchStatus: publicTemplatesFetchStatus, getTemplates: getPublicTemplates } = usePublicTemplates()
  const { assetsFetchStatus: videosFetchStatus, getAssets: getVideos } = useVideoAssets()
  const { VideosFetchStatus, getVideos: getPublicVideos } = usePublicVideos()
  return (
    <QueryFetchStatePage queryFetchStatus={playlistsFetchStatus} onRetry={getPlaylists}>
      <QueryFetchStatePage queryFetchStatus={publicPlaylistsFetchStatus} onRetry={getPublicPlaylists}>
        <QueryFetchStatePage queryFetchStatus={assetsFetchStatus} onRetry={getAssets}>
          <QueryFetchStatePage queryFetchStatus={publicAssetsFetchStatus} onRetry={getPublicAssets}>
            <QueryFetchStatePage queryFetchStatus={layoutsFetchStatus} onRetry={getLayouts}>
              <QueryFetchStatePage queryFetchStatus={templatesFetchStatus} onRetry={getTemplates}>
                <QueryFetchStatePage queryFetchStatus={publicTemplatesFetchStatus} onRetry={getPublicTemplates}>
                  <QueryFetchStatePage queryFetchStatus={videosFetchStatus} onRetry={getVideos}>
                    <QueryFetchStatePage queryFetchStatus={VideosFetchStatus} onRetry={getPublicVideos}>
                      <DialogProvider>
                        <SelectScreenContentDialogPresenter {...props} />
                      </DialogProvider>
                    </QueryFetchStatePage>
                  </QueryFetchStatePage>
                </QueryFetchStatePage>
              </QueryFetchStatePage>
            </QueryFetchStatePage>
          </QueryFetchStatePage>
        </QueryFetchStatePage>
      </QueryFetchStatePage>
    </QueryFetchStatePage>
  )
}

function SelectScreenContentDialogPresenter({
  screen,
  updateScreenPlaying,
  onClose,
  orgId,
}: Props): ReactNode {
  const getSelected = useSelectedContent()
  const [selected, setSelected] = useState<ScreenPlayingOptionType[]>(
    () => getSelected(screen),
  );

  const { log } = useGzLog();

  useEffect(() => {
    log({
      eventtype: "ScreenContentDialogOpened",
      name: "ScreenContentDialogOpened",
      payload: {
        screenId: screen.id,
        screenName: screen.name ?? "undefined",
      },
    });
  }, []);

  const { showErrorSnackbar } = useDialog();
  const { user } = useAuth();
  const { createPlaylist } = usePlaylists();

  const [duration, setDuration] = useState(getDuration(screen));
  const [imageFit, setImageFit] = useState(screen.image_fit);

  const [selectedType, setSelectedType] = useState<SelectedType | undefined>(
    getSelectedType(screen),
  );

  useEffect(() => {
    if (selected.isNotEmpty())
      setSelected([])
  }, [selectedType])

  const { openContentPreviewDialog } = useContentPreviewDialog();

  async function optionChange(
    opt: ScreenPlayingOptionType[],
    newDuration: number,
    fit?: string,
  ) {
    const oldOption = selected;
    const oldImageFit = imageFit;

    setSelected(opt);
    let _opt = opt[0];

    if (opt.isLongerThan(1)) {
      const _items: PlaylistItem[] = opt.map((o, index) => ({
        playOrder: index + 1,
        duration: newDuration,
        ...o,
        type: o.type as OmitLiteral<typeof o.type, "playlist">,
      }))
      const upsertData = {
        id: screen.id,
        name: `${screen?.name ?? ""} Playlist`,
        org_id: orgId,
        deleted: false,
        items: _items,
        is_smart: false,
      }
      createPlaylist(upsertData, {
        onSuccess: (data) => {
          _opt = {
            type: "playlist",
            ...data,
            items: _items,
          };
        },
        onError: () => {
          setSelected(oldOption);
        }
      });

      updateScreenPlaying(
        { screenId: screen.id, opt: _opt, imageFit: fit, orgId: orgId },
        {
          onSuccess() {
            if (_opt) {
              log({
                eventtype: "ScreenContentAssigned",
                name: "ScreenContentAssigned",
                payload: {
                  screenId: screen.id,
                  screenName: screen.name ?? "undefined",
                  contentType: _opt.type,
                  contentId: _opt.id,
                  contentName: _opt.name ?? "undefined",
                  userId: user?.id,
                  organizationId: orgId,
                },
              });
            } else {
              log({
                eventtype: "ScreenContentUnassigned",
                name: "ScreenContentUnassigned",
                payload: {
                  screenId: screen.id,
                  screenName: screen.name ?? "undefined",
                  userId: user?.id,
                  organizationId: orgId,
                },
              });
            }
          },
          onError() {
            setImageFit(oldImageFit);
            setSelected(oldOption);
            showErrorSnackbar("Something went wrong");
          },
        },
      );
    }
  }

  async function onPreview(
    opt: ScreenPlayingOptionType,
  ) {
    if (!user?.id) return;

    openContentPreviewDialog({
      userId: user?.id,
      duration: duration,
      imageFit: imageFit ?? "none",
      orgId: orgId,
      onChange: (val, openInNew) => {
        setSelected([val]);
        if (openInNew) {
          window.open(`/layout?id=${val.id}`, "_blank");
        }
      },
      opt: opt,
      screen: screen,
      updateScreenPlaying: updateScreenPlaying,
    });

    log({
      eventtype: "ScreenContentPreviewed",
      name: "ScreenContentPreviewed",
      payload: {
        screenId: screen.id,
        screenName: screen.name ?? "undefined",
        previewedContentType: opt.type,
        previewedContentId: opt.id,
      },
    });
  }

  const [index, setIndex] = useState(0);

  function _getTypeContent(type?: SelectedType, listOnly = false) {
    switch (type) {
      case "asset":
        return (
          <SelectImages
            listOnly={listOnly}
            duration={duration}
            setDuration={(val) => {
              if (!selected) return;
              setDuration(val);
              optionChange(selected, val);
            }}
            imageFit={imageFit ?? "none"}
            setImageFit={(val) => {
              if (!val || !selected) return;
              setImageFit(val);
              optionChange(selected, duration, val);
            }}
            selected={selected}
            onSelected={(assets) => {
              optionChange(
                assets,
                duration,
              )
            }}
          />
        );
      case "layout":
        return (
          <SelectCustomLayout
            screenInfo={screen}
            listOnly={listOnly}
            selected={selected.find(s => s.type === "layout")}
            onPreview={onPreview}
            onSelected={(val) => {
              optionChange([val], duration);
            }}
          />
        );
      case "playlist":
        return (
          <SelectPlaylist
            listOnly={listOnly}
            selected={selected.find(s => s.type === "playlist")}
            onPreview={onPreview}
            onSelected={(val) => {
              optionChange([val], duration);
            }}
          />
        );
      case "video":
        return (
          <SelectVideo
            listOnly={listOnly}
            selected={selected}
            onSelected={(videos) => {
              optionChange(
                videos,
                duration,
              );
            }}
          />
        );

      default:
        return <></>;
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-2 py-4">
      {
        screen.location_id &&
        <div className="flex justify-end w-full">
          <SyncContentWithLocationButton screenId={screen.id} />
        </div>
      }
      <div className="h-full flex-1 overflow-y-auto">
        <CustomCarousel
          hideButtons
          hideProgress
          selectedIndex={index}
          items={[
            <TypeSelector
              key={`type_selector`}
              setSelectedType={setSelectedType}
              selectedType={selectedType}
              getTypeContent={_getTypeContent}
            />,
            <div
              className="flex h-full max-h-[60vh] w-full flex-col items-center justify-center"
              key={"type_content"}
            >
              {_getTypeContent(selectedType)}
            </div>,
          ]}
        />
      </div>
      <div className="flex items-center justify-between">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (index === 0) {
              log({
                eventtype: "ScreenContentSelectionSkipped",
                name: "ScreenContentSelectionSkipped",
                payload: {
                  screenId: screen.id,
                  screenName: screen.name ?? "undefined",
                },
              });
              onClose();
            } else {
              setIndex(0);
            }
          }}
        >
          {index == 0 ? `Skip For Now` : `Previous`}
        </Button>

        <div className="flex gap-2">
          {!!selected.length && (
            <Button
              variant="outline"
              onClick={() => {
                if (!selected.length) return;
                if (selected.length === 1) return onPreview(selected[0]);
                onPreview({
                  type: "playlist",
                  id: screen.id,
                  created_at: null,
                  deleted: false,
                  is_smart: true,
                  name: `${screen?.name ?? ""} Playlist`,
                  org_id: orgId,
                  thumbnail: null,
                  transition: null,
                  updated_at: null,
                  url: null,
                  items: selected
                    .map((s, i) => ({
                      ...s,
                      duration,
                      playOrder: i + 1,
                      type: s?.type as OmitLiteral<typeof s.type, "playlist">,
                    })),
                });
              }}
              className="w-24"
              size="sm"
            >
              <MdRemoveRedEye />
              Preview
            </Button>
          )}
          <Button
            onClick={() => {
              if (index === 1) {
                log({
                  eventtype: "ScreenContentSelectionDone",
                  name: "ScreenContentSelectionDone",
                  payload: {
                    screenId: screen.id,
                    screenName: screen.name ?? "undefined",
                  },
                });
                onClose();
              } else setIndex(1);
            }}
            className="w-24"
            size="sm"
          >
            {index == 1 ? <MdDone /> : <MdArrowForward />}
            {index == 1 ? `Done` : `Next`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TypeSelector({
  setSelectedType,
  selectedType,
  getTypeContent,
}: {
  selectedType?: SelectedType;
  setSelectedType: (val?: SelectedType) => void;
  getTypeContent: (type?: SelectedType, listOnly?: boolean) => React.ReactNode;
}) {
  const { openDialog } = useDialog()
  const isFeatureAllowed = useIsFeatureAllowed()
  const [isFlipped, setIsFlipped] = useState<Record<SelectedType, boolean>>({
    asset: false,
    layout: false,
    playlist: false,
    video: false,
  });
  function _getTypeIcon(type: SelectedType) {
    switch (type) {
      case "asset":
        return <BsImages />;
      case "layout":
        return <BsLayoutWtf />;
      case "playlist":
        return <MdOutlinePlaylistPlay />;
      case "video":
        return <ImFileVideo />;

      default:
        return <></>;
    }
  }

  function _getTypeLabel(type: SelectedType) {
    switch (type) {
      case "asset":
        return "One or more images";
      case "layout":
        return "Custom Design";
      case "playlist":
        return "Smart Playlist";
      case "video":
        return "One or more Videos";

      default:
        return "-";
    }
  }

  function _getTypeInfo(type: SelectedType) {
    switch (type) {
      case "asset":
        return "You can select one or more images and play them in a slideshow.";
      case "layout":
        return "Design a custom layout from scratch or select from our free design library to match your needs.";
      case "playlist":
        return "Combine Images, Videos and your Custom designs as a Smart Playlist. Define rules fow when the content has to be displayed.";
      case "video":
        return "Upload and manage your video content. Supported formats include MP4, WebM, and MOV.";

      default:
        return "-";
    }
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden">
      <div className="grid h-full max-h-[32rem] w-full max-w-lg grid-cols-2 gap-4 overflow-hidden">
        {_types.map((type, idx) => (
          <button
            key={`key_${idx}`}
            onClick={() => {
              // Check if feature is allowed based on the plan
              if (!isFeatureAllowed(type)) {
                // Show upgrade dialog
                openDialog({
                  title: "Feature Not Available",
                  customContent: (
                    <div className="flex flex-col gap-2 pt-2">
                      <p>
                        This feature is not available on your current plan. Please upgrade your plan to access this feature.
                      </p>

                      <div className="flex w-full items-center justify-end">
                        <Button asChild>
                          <Link href={"/change_plan"}>Upgrade Options</Link>
                        </Button>
                      </div>
                    </div>
                  ),
                });
                return;
              }

              setIsFlipped((isFlipped) => ({
                ...isFlipped,
                [type]: false,
              }));
              setSelectedType(type);
            }}
            className={`group relative flex h-full w-full flex-col items-center justify-center gap-4 overflow-clip rounded-lg border-2 text-sm font-semibold transition-all [perspective:1000px] hover:border-accentColor disabled:text-gray-500 disabled:hover:border-transparent dark:hover:border-darkAccentColor ${type === selectedType
              ? "border-accentColor dark:border-darkAccentColor"
              : "border-transparent dark:border-transparent"
              }`}
          >
            <div className="absolute inset-0 w-full h-full overflow-hidden">
              <div className="w-full h-full blur-[.5px]">
                {getTypeContent(type, true)}
              </div>
            </div>
            <div
              className={`relative h-full w-full transition-all duration-500 [transform-style:preserve-3d] ${isFlipped[type] ? "[transform:rotateY(180deg)]" : ""
                }`}
            >
              <div className="relative flex h-full w-full flex-col items-center justify-center gap-4 bg-lightCard/60 dark:bg-darkCard/60">
                <CustomIcon>{_getTypeIcon(type)}</CustomIcon>
                {_getTypeLabel(type)}
                <div
                  className="absolute right-4 top-4 cursor-pointer text-lg text-gray-600 dark:text-gray-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsFlipped((isFlipped) => ({
                      ...isFlipped,
                      [type]: true,
                    }));
                  }}
                >
                  <MdInfoOutline />
                </div>
              </div>

              <div className="absolute inset-0 h-full w-full rounded-xl bg-lightCard text-center [transform:rotateY(180deg)] [backface-visibility:hidden] dark:bg-darkCard">
                <div className="relative h-full w-full">
                  <div className="flex h-full w-full flex-col items-center justify-center p-2">
                    {_getTypeInfo(type)}
                  </div>
                  <div
                    className="absolute right-4 top-4 text-lg text-gray-600 dark:text-gray-400"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFlipped((isFlipped) => ({
                        ...isFlipped,
                        [type]: false,
                      }));
                    }}
                  >
                    <MdClear />
                  </div>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CustomIcon({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-full bg-accentColor bg-opacity-5 p-6 text-6xl text-accentColor dark:bg-darkAccentColor dark:bg-opacity-5 dark:text-darkAccentColor">
      {children}
    </div>
  );
}

function getDuration(screen: ScreenWithPlayingInfo) {
  const _items = (screen.playlists?.items ?? []) as PlaylistItem[];
  if (_items.length > 0) {
    return _items[0].duration;
  }
  return 10;
}

export function useSelectedContent() {
  const { assets: _videos } = useVideoAssets()
  const { Videos: publicVideos } = usePublicVideos()
  const { assets } = useAssets()
  const { assets: publicAssets } = usePublicAssets()
  const { layouts } = useLayouts()
  const { templates } = useTemplates()
  const { playlists: _playlists } = usePlaylists()
  const { playlists: publicPlaylists } = usePublicPlaylists()
  const playlists = [..._playlists, ...publicPlaylists]
  const videos = [..._videos, ...publicVideos]
  function getSelected(
    screen: ScreenWithPlayingInfo,
  ): ScreenPlayingOptionType[] {
    if (screen.asset_id) {
      const asset = assets.find((v) => v.id === screen.asset_id) ?? publicAssets.find((v) => v.id === screen.asset_id)
      if (!asset) return [];
      return [{
        type: "asset",
        ...asset,
      }];
    }

    if (screen.layout_id) {
      const layout = layouts.find((v) => v.id === screen.layout_id)
      if (!layout) return [];
      return [{
        type: "layout",
        ...layout,
      }];
    }

    if (screen.template_id) {
      const template = templates.find((v) => v.id === screen.template_id)
      if (!template) return [];
      return [{
        type: "template",
        ...template,
      }];
    }

    if (screen.video_id) {
      const video = videos.find((v) => v.id === screen.video_id)
      if (!video) return [];
      return [{
        type: "video",
        ...video,
      }];
    }

    if (screen.playlist_id) {
      const playlist = playlists?.find(
        (v) => v.id === screen.playlist_id,
      );
      if (!playlist) return [];
      return [{
        type: "playlist",
        ...playlist,
      }];
    }

    return [];
  }

  return getSelected;
}

function getSelectedType(
  screen: ScreenWithPlayingInfo,
): SelectedType | undefined {
  if (screen.asset_id) {
    return "asset";
  }

  if (screen.layout_id) return "layout";
  if (screen.template_id) return "layout";

  if (screen.playlist_id) {
    if (screen.playlist_id === screen.id) return "asset";
    return "playlist";
  }

  return undefined;
}

function useIsFeatureAllowed() {
  const { planProperties } = useUserSubscriptions();

  return (feature: SelectedType) => {
    // If allowed_features is not defined/null, all features are allowed (for backward compatibility)
    if (!planProperties.allowed_features) return true;

    // Check if the specific feature is allowed
    switch (feature) {
      case "asset": // images
        return planProperties.allowed_features.images !== false;
      case "video":
        return planProperties.allowed_features.videos !== false;
      case "playlist":
        return planProperties.allowed_features.playlists !== false;
      case "layout": // custom designs (layouts/templates)
        return planProperties.allowed_features.customDesigns !== false;
      default:
        return true;
    }
  };
}

export default SelectScreenContentDialog;
